import { ApiResponse } from '@shared/types/api'
import { cognitoAuthService } from '@/services/cognito-auth.service'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

// Get token from Cognito session
async function getToken(): Promise<string | null> {
  try {
    return await cognitoAuthService.getToken()
  } catch {
    return null
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public code: string,
    public details?: Record<string, unknown>
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`
  const token = await getToken()

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  })

  const data: ApiResponse<T> = await response.json()

  if (!data.success || !response.ok) {
    throw new ApiError(
      data.error?.message || 'Request failed',
      response.status,
      data.error?.code || 'UNKNOWN_ERROR',
      data.error?.details
    )
  }

  return data.data as T
}

export const apiClient = {
  get: <T>(endpoint: string, token?: string) =>
    fetchApi<T>(endpoint, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }),

  post: <T>(endpoint: string, body: unknown, token?: string) =>
    fetchApi<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }),

  put: <T>(endpoint: string, body: unknown, token?: string) =>
    fetchApi<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }),

  delete: <T>(endpoint: string, token?: string) =>
    fetchApi<T>(endpoint, {
      method: 'DELETE',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }),
}
